// Staging-table suffix keyed by target platform identifier — either a CRM
// target_type (e.g. "salesforce", "dynamics365") or an RDBMS db_type (e.g.
// "oracle", "postgres"), matching whichever mode the Data Converter's
// Target Platform step is in.
//
// "_sf" was historically hardcoded for every target regardless of platform
// (Create Tables always emitted `${target_object}_sf`, and every frontend
// call site that builds a target table name repeated the same literal).
// That broke down for a plain Oracle/Postgres/etc. DB->DB migration, which
// has nothing to do with Salesforce. Keep this map in sync with
// STAGING_TABLE_SUFFIXES in ztd_app/api/modules/data_converter/service.py —
// there's no code sharing between the Python backend and this TS frontend,
// so both copies must be updated together. The backend resolves db_type
// aliases (postgres/sqlserver) via ztd_sdk.connectors.normalize_db_type
// before its own lookup; this frontend has no equivalent alias registry,
// so both the canonical and alias forms are listed directly below instead.
const STAGING_TABLE_SUFFIXES: Record<string, string> = {
  salesforce: "_sf",
  dynamics365: "_dyn",
  power_platform: "_pp",
  workday: "_wd",
  oracle: "_ora",
  postgres: "_pg",
  postgresql: "_pg",
  snowflake: "_sfk",
  mysql: "_my",
  mssql: "_mss",
  sqlserver: "_mss",
  synapse: "_syn",
  databricks: "_dbx",
  bigquery: "_bq",
};

// Historical universal default — kept as the fallback for an
// unknown/unset target platform so existing behavior doesn't change when
// the platform can't be determined yet (e.g. before a target is connected).
const DEFAULT_STAGING_TABLE_SUFFIX = "_sf";

/**
 * Staging-table suffix for a target platform identifier (CRM target_type or
 * RDBMS db_type), case-insensitive. Falls back to "_sf" for an unknown or
 * unset platform.
 *
 * IMPORTANT: every place in this app that builds a target/staging table
 * name for the same migration job must call this with the SAME platform
 * value Create Tables (`generate_ddl` / /ddl/generate) used — otherwise
 * Create Tables and Load Data/Reconciliation/SF Load will disagree on the
 * table name and every load will fail with "table or view does not exist".
 */
export function stagingTableSuffix(
  targetPlatform: string | null | undefined,
): string {
  if (!targetPlatform) return DEFAULT_STAGING_TABLE_SUFFIX;
  return (
    STAGING_TABLE_SUFFIXES[targetPlatform.trim().toLowerCase()] ??
    DEFAULT_STAGING_TABLE_SUFFIX
  );
}
